/**
 * Department Form Component
 * Reusable form for creating and editing departments
 * Aligned with backend Department table structure
 */

import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Alert,
  Button,
  CircularProgress,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  Department,
  DepartmentFormData,
  SafetyCharacteristicType,
  SAFETY_CHARACTERISTIC_LABELS,
} from '../../../types';
import { departmentService } from '../../../services/departmentService';
import { unitService } from '../../../services/unitService';
import { Unit } from '../../../types/unit';
import { CompanySelectionModal, CompanyInfoBox } from '../../../components/forms';
import { useCompanySelection } from '../../../hooks/useCompanySelection';

// Form validation schema (aligned with backend Department table)
const departmentSchema = yup.object({
  name: yup
    .string()
    .required('Nome do setor é obrigatório')
    .min(2, 'Mínimo 2 caracteres')
    .max(100, 'Máximo 100 caracteres'),
  unitId: yup
    .number()
    .nullable(),
  companyId: yup
    .string()
    .required('Empresa é obrigatória'),
  
  // Location fields
  location: yup.string().max(255, 'Máximo 255 caracteres'),
  construction: yup.string().max(255, 'Máximo 255 caracteres'),
  ceilingHeight: yup.string().max(100, 'Máximo 100 caracteres'),
  flooring: yup.string().max(100, 'Máximo 100 caracteres'),
  roofing: yup.string().max(255, 'Máximo 255 caracteres'),
  ventilation: yup.string().max(255, 'Máximo 255 caracteres'),
  lighting: yup.string().max(255, 'Máximo 255 caracteres'),
  shift: yup.string().max(100, 'Máximo 100 caracteres'),
  
  // Safety characteristics (object with boolean values for switches)
  safetyCharacteristics: yup.object().shape({
    [SafetyCharacteristicType.FOOD_HANDLING]: yup.boolean().default(false),
    [SafetyCharacteristicType.ELECTRICAL]: yup.boolean().default(false),
    [SafetyCharacteristicType.PEMT]: yup.boolean().default(false),
    [SafetyCharacteristicType.CONFINED_SPACE]: yup.boolean().default(false),
    [SafetyCharacteristicType.HEIGHT_WORK]: yup.boolean().default(false),
    [SafetyCharacteristicType.HOT_WORK]: yup.boolean().default(false),
    [SafetyCharacteristicType.FORKLIFT]: yup.boolean().default(false),
    [SafetyCharacteristicType.SELF_PROPELLED_MACHINE]: yup.boolean().default(false),
  }).default({
    [SafetyCharacteristicType.FOOD_HANDLING]: false,
    [SafetyCharacteristicType.ELECTRICAL]: false,
    [SafetyCharacteristicType.PEMT]: false,
    [SafetyCharacteristicType.CONFINED_SPACE]: false,
    [SafetyCharacteristicType.HEIGHT_WORK]: false,
    [SafetyCharacteristicType.HOT_WORK]: false,
    [SafetyCharacteristicType.FORKLIFT]: false,
    [SafetyCharacteristicType.SELF_PROPELLED_MACHINE]: false,
  }),
}).required();

interface DepartmentFormProps {
  department?: Department;
  onSubmit: (data: DepartmentFormData) => Promise<void>;
  onCancel?: () => void;
  loading?: boolean;
  error?: string | null;
  mode?: 'create' | 'edit';
  preSelectedCompanyId?: string;
}

const DepartmentForm: React.FC<DepartmentFormProps> = ({
  department,
  onSubmit,
  onCancel,
  loading = false,
  error,
  mode = 'create',
  preSelectedCompanyId,
}) => {
  const [nameValidationError, setNameValidationError] = useState<string | null>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [units, setUnits] = useState<Unit[]>([]);
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [newUnitName, setNewUnitName] = useState('');
  const [newUnitLocation, setNewUnitLocation] = useState('');
  const [creatingUnit, setCreatingUnit] = useState(false);
  const {
    modalOpen,
    selectedCompany,
    showInfoBox,
    openModal,
    handleSelect: handleCompanySelect,
  } = useCompanySelection({ isEditMode: mode === 'edit', currentCompanyId: String(department?.companyId ?? preSelectedCompanyId ?? '') });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<DepartmentFormData>({
    resolver: yupResolver(departmentSchema) as any,
    defaultValues: {
      name: '',
      unitId: null,
      companyId: preSelectedCompanyId || '',
      
      location: '',
      construction: '',
      ceilingHeight: '',
      flooring: '',
      roofing: '',
      ventilation: '',
      lighting: '',
      shift: '',
      
      safetyCharacteristics: {
        [SafetyCharacteristicType.FOOD_HANDLING]: false,
        [SafetyCharacteristicType.ELECTRICAL]: false,
        [SafetyCharacteristicType.PEMT]: false,
        [SafetyCharacteristicType.CONFINED_SPACE]: false,
        [SafetyCharacteristicType.HEIGHT_WORK]: false,
        [SafetyCharacteristicType.HOT_WORK]: false,
        [SafetyCharacteristicType.FORKLIFT]: false,
        [SafetyCharacteristicType.SELF_PROPELLED_MACHINE]: false,
      },
    },
  });

  // Watch form values for validation
  const watchedName = watch('name');
  const watchedCompanyId = watch('companyId');

  // Carregar unidades da empresa selecionada (para o select de Unidade)
  useEffect(() => {
    if (watchedCompanyId) {
      unitService.getUnitsByCompanyId(watchedCompanyId)
        .then(setUnits)
        .catch(() => setUnits([]));
    } else {
      setUnits([]);
    }
  }, [watchedCompanyId]);

  // Criar unidade inline e selecioná-la
  const handleCreateUnit = async () => {
    if (!watchedCompanyId || !newUnitName.trim()) return;
    setCreatingUnit(true);
    try {
      const created = await unitService.createUnit({
        companyId: Number(watchedCompanyId),
        name: newUnitName.trim(),
        location: newUnitLocation.trim() || undefined,
      });
      setUnits((prev) => [...prev, created]);
      setValue('unitId', created.id);
      enqueueSnackbar('Unidade criada', { variant: 'success' });
      setUnitDialogOpen(false);
      setNewUnitName('');
      setNewUnitLocation('');
    } catch (err: unknown) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Erro ao criar unidade', { variant: 'error' });
    } finally {
      setCreatingUnit(false);
    }
  };

  // Sync company selection (from modal or auto-fill) to form
  useEffect(() => {
    if (selectedCompany && selectedCompany.id) {
      const currentValue = watch('companyId');
      if (String(currentValue) !== selectedCompany.id) {
        setValue('companyId', selectedCompany.id);
      }
    }
  }, [selectedCompany, watch, setValue]);

  // Load department data when editing
  useEffect(() => {
    if (department && mode === 'edit') {
      // Convert array of characteristics to object for switches
      const characteristicsObj = {
        [SafetyCharacteristicType.FOOD_HANDLING]: false,
        [SafetyCharacteristicType.ELECTRICAL]: false,
        [SafetyCharacteristicType.PEMT]: false,
        [SafetyCharacteristicType.CONFINED_SPACE]: false,
        [SafetyCharacteristicType.HEIGHT_WORK]: false,
        [SafetyCharacteristicType.HOT_WORK]: false,
        [SafetyCharacteristicType.FORKLIFT]: false,
        [SafetyCharacteristicType.SELF_PROPELLED_MACHINE]: false,
      };

      // Mark as true the characteristics that exist
      department.safetyCharacteristics?.forEach(char => {
        characteristicsObj[char.characteristic] = true;
      });

      reset({
        name: department.name ?? '',
        unitId: department.unitId ?? null,
        companyId: department.companyId ?? '',
        
        location: department.location ?? '',
        construction: department.construction ?? '',
        ceilingHeight: department.ceilingHeight ?? '',
        flooring: department.flooring ?? '',
        roofing: department.roofing ?? '',
        ventilation: department.ventilation ?? '',
        lighting: department.lighting ?? '',
        shift: department.shift ?? '',
        
        safetyCharacteristics: characteristicsObj,
      });
    }
  }, [department, mode, reset]);

  // Validate department name uniqueness
  useEffect(() => {
    const validateName = async () => {
      if (!watchedName || !watchedCompanyId || watchedName.length < 2) {
        setNameValidationError(null);
        return;
      }

      try {
        const result = await departmentService.validateDepartmentName(
          watchedName,
          watchedCompanyId,
          department?.id
        );
        
        if (!result.valid) {
          setNameValidationError(result.message ?? 'Nome já existe na empresa');
        } else {
          setNameValidationError(null);
        }
      } catch (error) {
        console.error('Error validating department name:', error);
      }
    };

    const timeoutId = setTimeout(validateName, 500); // Debounce validation
    return () => clearTimeout(timeoutId);
  }, [watchedName, watchedCompanyId, department?.id]);

  const handleFormSubmit = async (data: DepartmentFormData) => {
    if (nameValidationError) {
      return; // Don't submit if name validation failed
    }

    try {
      await onSubmit(data);
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <>
    {showInfoBox && selectedCompany && (
      <CompanyInfoBox
        companyName={selectedCompany.name}
        onEdit={openModal}
      />
    )}
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      {/* Basic Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
          Informações Básicas
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nome do Setor *"
                  fullWidth
                  error={!!errors.name || !!nameValidationError}
                  helperText={errors.name?.message || nameValidationError}
                  placeholder="Ex: Produção, Administração, RH"
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="unitId"
              control={control}
              render={({ field }) => {
                const value = units.find((u) => Number(u.id) === Number(field.value)) ?? null;
                return (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Autocomplete
                      sx={{ flex: 1 }}
                      options={units}
                      getOptionLabel={(o) => o.name}
                      isOptionEqualToValue={(o, v) => o.id === v.id}
                      value={value}
                      disabled={!watchedCompanyId}
                      onChange={(_e, selected) => field.onChange(selected ? selected.id : null)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Unidade (opcional)"
                          placeholder={watchedCompanyId ? 'Selecione ou crie' : 'Selecione a empresa primeiro'}
                          helperText="Riscos, exames e fontes aplicados à unidade valem para este setor."
                        />
                      )}
                    />
                    <Button
                      variant="outlined"
                      sx={{ mt: 0.5, whiteSpace: 'nowrap' }}
                      disabled={!watchedCompanyId}
                      onClick={() => setUnitDialogOpen(true)}
                    >
                      Nova
                    </Button>
                  </Box>
                );
              }}
            />
          </Grid>

        </Grid>
      </Box>
      {/* Location Information */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
          Localização e Estrutura
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Localização"
                  fullWidth
                  error={!!errors.location}
                  helperText={errors.location?.message}
                  placeholder="Ex: Prédio A, 2º andar, Ala Norte"
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="construction"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Tipo de Construção"
                  fullWidth
                  error={!!errors.construction}
                  helperText={errors.construction?.message}
                  placeholder="Ex: Alvenaria, Estrutura metálica"
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="ceilingHeight"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Altura do Teto"
                  fullWidth
                  error={!!errors.ceilingHeight}
                  helperText={errors.ceilingHeight?.message}
                  placeholder="Ex: 3,5m"
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="flooring"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Piso"
                  fullWidth
                  error={!!errors.flooring}
                  helperText={errors.flooring?.message}
                  placeholder="Ex: Concreto polido, Cerâmica"
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="roofing"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Tipo de Cobertura"
                  fullWidth
                  error={!!errors.roofing}
                  helperText={errors.roofing?.message}
                  placeholder="Ex: Telha cerâmica, Laje"
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="ventilation"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Ventilação"
                  fullWidth
                  error={!!errors.ventilation}
                  helperText={errors.ventilation?.message}
                  placeholder="Ex: Natural, Ar condicionado, Exaustores"
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="lighting"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Iluminação"
                  fullWidth
                  error={!!errors.lighting}
                  helperText={errors.lighting?.message}
                  placeholder="Ex: Natural + LED, Fluorescente"
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 4
            }}>
            <Controller
              name="shift"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Turno"
                  fullWidth
                  error={!!errors.shift}
                  helperText={errors.shift?.message}
                  placeholder="Ex: 1º turno, 06h às 14h"
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>
      {/* Safety Characteristics */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontSize: '1.5rem' }}>
          Atividades Especiais (NR)
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Marque as características de segurança aplicáveis a este setor
        </Typography>
        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name={`safetyCharacteristics.${SafetyCharacteristicType.FOOD_HANDLING}`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={SAFETY_CHARACTERISTIC_LABELS[SafetyCharacteristicType.FOOD_HANDLING]}
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name={`safetyCharacteristics.${SafetyCharacteristicType.ELECTRICAL}`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={SAFETY_CHARACTERISTIC_LABELS[SafetyCharacteristicType.ELECTRICAL]}
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name={`safetyCharacteristics.${SafetyCharacteristicType.PEMT}`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={SAFETY_CHARACTERISTIC_LABELS[SafetyCharacteristicType.PEMT]}
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name={`safetyCharacteristics.${SafetyCharacteristicType.CONFINED_SPACE}`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={SAFETY_CHARACTERISTIC_LABELS[SafetyCharacteristicType.CONFINED_SPACE]}
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name={`safetyCharacteristics.${SafetyCharacteristicType.HEIGHT_WORK}`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={SAFETY_CHARACTERISTIC_LABELS[SafetyCharacteristicType.HEIGHT_WORK]}
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name={`safetyCharacteristics.${SafetyCharacteristicType.HOT_WORK}`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={SAFETY_CHARACTERISTIC_LABELS[SafetyCharacteristicType.HOT_WORK]}
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name={`safetyCharacteristics.${SafetyCharacteristicType.FORKLIFT}`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={SAFETY_CHARACTERISTIC_LABELS[SafetyCharacteristicType.FORKLIFT]}
                />
              )}
            />
          </Grid>

          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name={`safetyCharacteristics.${SafetyCharacteristicType.SELF_PROPELLED_MACHINE}`}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Switch checked={field.value} onChange={field.onChange} />}
                  label={SAFETY_CHARACTERISTIC_LABELS[SafetyCharacteristicType.SELF_PROPELLED_MACHINE]}
                />
              )}
            />
          </Grid>

        </Grid>
      </Box>
      {/* Action Buttons */}
      <Box display="flex" justifyContent="flex-end" gap={2} pt={2}>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} disabled={loading}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variant="contained"
          disabled={loading || !!nameValidationError}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {loading ? 'Salvando...' : mode === 'edit' ? 'Atualizar' : 'Criar'}
        </Button>
      </Box>
    </form>
    <CompanySelectionModal
      open={modalOpen}
      onSelect={handleCompanySelect}
    />
    <Dialog open={unitDialogOpen} onClose={() => setUnitDialogOpen(false)} fullWidth maxWidth="sm">
      <DialogTitle>Nova Unidade</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Nome" fullWidth required size="small" value={newUnitName}
            onChange={(e) => setNewUnitName(e.target.value)}
          />
          <TextField
            label="Localização" fullWidth size="small" value={newUnitLocation}
            placeholder="Endereço / referência do posto"
            onChange={(e) => setNewUnitLocation(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setUnitDialogOpen(false)} disabled={creatingUnit}>Cancelar</Button>
        <Button variant="contained" onClick={handleCreateUnit} disabled={creatingUnit || !newUnitName.trim()}>
          {creatingUnit ? 'Criando…' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default DepartmentForm;